/**
 * Drives the stack pinned to the top of the page - the announcement bar and the
 * header - from a single passive scroll listener, so the two can never disagree
 * about which way the page is moving.
 *
 * What is allowed to move is decided in Liquid and handed over as data
 * attributes; this file only decides when. Everything it does is a class
 * toggle, so the movement itself stays in CSS (transforms, no layout shift).
 */
class StickyStack extends HTMLElement {
  constructor() {
    super();

    this.schedule = this.schedule.bind(this);
    this.refresh = this.refresh.bind(this);
    this.reset = this.reset.bind(this);
    this.onHold = this.onHold.bind(this);
    this.onRelease = this.onRelease.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onDeselect = this.onDeselect.bind(this);
  }

  connectedCallback() {
    this.breakpoint = parseInt(this.dataset.breakpoint, 10) || 768;
    this.headerHide = this.dataset.headerHide || 'none';

    this.frame = null;
    this.stowed = false;
    this.held = false;
    this.pinned = false;
    this.lastY = this.pageY;

    this.refresh();

    window.addEventListener('scroll', this.schedule, { passive: true });
    window.addEventListener('resize', this.schedule, { passive: true });
    window.addEventListener('orientationchange', this.reset);
    // A restored scroll position, an anchor jump or a step through the history
    // all land the customer somewhere new without a direction to read.
    window.addEventListener('pageshow', this.reset);
    window.addEventListener('hashchange', this.reset);
    window.addEventListener('popstate', this.reset);

    // Anything running its own scroll animation can freeze the stack for the
    // duration, so what it measured before it started still holds afterwards.
    document.addEventListener('sticky-stack:hold', this.onHold);
    document.addEventListener('sticky-stack:release', this.onRelease);

    document.addEventListener('shopify:section:load', this.refresh);
    document.addEventListener('shopify:section:unload', this.refresh);
    document.addEventListener('shopify:section:reorder', this.refresh);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      document.addEventListener('shopify:block:select', this.onSelect);
      document.addEventListener('shopify:block:deselect', this.onDeselect);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.schedule);
    window.removeEventListener('resize', this.schedule);
    window.removeEventListener('orientationchange', this.reset);
    window.removeEventListener('pageshow', this.reset);
    window.removeEventListener('hashchange', this.reset);
    window.removeEventListener('popstate', this.reset);
    document.removeEventListener('sticky-stack:hold', this.onHold);
    document.removeEventListener('sticky-stack:release', this.onRelease);
    document.removeEventListener('shopify:section:load', this.refresh);
    document.removeEventListener('shopify:section:unload', this.refresh);
    document.removeEventListener('shopify:section:reorder', this.refresh);
    document.removeEventListener('shopify:section:select', this.onSelect);
    document.removeEventListener('shopify:section:deselect', this.onDeselect);
    document.removeEventListener('shopify:block:select', this.onSelect);
    document.removeEventListener('shopify:block:deselect', this.onDeselect);

    if (this.frame) cancelAnimationFrame(this.frame);

    // Leave nothing hidden behind for whoever renders next.
    this.stowed = false;
    this.apply();
  }

  get isMobile() {
    return window.innerWidth < this.breakpoint;
  }

  get pageY() {
    return Math.max(0, window.scrollY);
  }

  /** Re-reads the stack, which the theme editor can replace at any time. */
  refresh() {
    this.barSection = document.querySelector('.topbar-section');
    this.headerSection = document.querySelector('.header-section');

    var topbar = this.barSection ? this.barSection.querySelector('[data-topbar-hide-after]') : null;
    this.barHides = !!topbar && topbar.dataset.topbarHideOnScroll === 'true';
    this.threshold = topbar ? parseInt(topbar.dataset.topbarHideAfter, 10) || 0 : 0;

    this.schedule();
  }

  /** Back to "everything visible", with no stale direction left over. */
  reset() {
    this.lastY = this.pageY;
    this.stowed = false;
    this.apply();
    this.schedule();
  }

  onHold() {
    this.held = true;
  }

  onRelease() {
    this.held = false;
    this.lastY = this.pageY;
  }

  onSelect(event) {
    if (!this.owns(event.target)) return;
    this.pinned = true;
    this.schedule();
  }

  onDeselect(event) {
    if (!this.owns(event.target)) return;
    this.pinned = false;
    this.schedule();
  }

  owns(element) {
    if (!element) return false;
    return (
      (this.barSection && this.barSection.contains(element)) ||
      (this.headerSection && this.headerSection.contains(element))
    );
  }

  schedule() {
    if (this.frame === null) this.frame = requestAnimationFrame(this.update.bind(this));
  }

  update() {
    this.frame = null;

    var y = this.pageY;
    var delta = y - this.lastY;

    // Frozen by a scroll animation: keep the state that animation measured
    // against, but stay in step with where the page actually is.
    if (this.held) {
      this.lastY = y;
      return;
    }

    if (this.pinned) {
      this.lastY = y;
      this.stowed = false;
      this.apply();
      return;
    }

    // A move this big in one frame is a jump, not the customer changing their
    // mind, so it sets the baseline instead of a direction.
    var jumped = Math.abs(delta) > window.innerHeight;

    if (y <= this.threshold) {
      this.stowed = false;
    }
    else if (!jumped && Math.abs(delta) >= StickyStack.JITTER) {
      this.stowed = delta > 0;
    }

    // Below the jitter threshold lastY is left alone, so slow, steady scrolling
    // still adds up to a direction instead of being ignored frame by frame.
    if (jumped || y <= this.threshold || Math.abs(delta) >= StickyStack.JITTER) {
      this.lastY = y;
    }

    this.apply();
  }

  apply() {
    var mobile = this.isMobile;
    var stowBar = this.stowed && this.barHides && mobile;
    var hideHeader =
      this.stowed &&
      (this.headerHide === 'always' || (this.headerHide === 'desktop' && !mobile));

    if (this.barSection) this.barSection.classList.toggle('topbar-stowed', stowBar);
    if (this.headerSection) this.headerSection.classList.toggle('header-stowed', hideHeader);

    if (stowBar === this.barStowed && hideHeader === this.headerStowed) return;

    this.barStowed = stowBar;
    this.headerStowed = hideHeader;

    // Whatever docks below the stack has to re-measure now, not on the next
    // scroll: the customer may well have stopped moving already.
    document.dispatchEvent(new CustomEvent('sticky-stack:change'));
  }
}

StickyStack.JITTER = 4;

customElements.define('sticky-stack', StickyStack);
